import {
	type Choice,
	type Field,
	fieldTypeSchema,
	type WizardFormConfig,
} from "@CommonLib/src/frontend/WizardForm/types";
import {
	fieldsFromStepId,
	getChoicesFromField,
	stepsUsingChoiceAsCondition,
	stepsUsingFieldAsCondition,
} from "@CommonLib/src/frontend/WizardForm/utils";
import {
	Add,
	ArrowDownward,
	ArrowUpward,
	Close,
	Delete,
	Edit,
	Save,
} from "@mui/icons-material";
import {
	Box,
	Button,
	CardContent,
	Checkbox,
	Chip,
	FormControlLabel,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Menu,
	MenuItem,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useState } from "react";
import { match } from "ts-pattern";

interface EditChoiceProps {
	choice: Choice;
	field: Field;
	selectedStepId: number;
	onEditChoice: (
		stepId: number,
		fieldId: number,
		choiceId: number,
		updates: Partial<Choice>,
	) => void;
	setEditing: React.Dispatch<React.SetStateAction<boolean>>;
}

const EditChoice: React.FC<EditChoiceProps> = ({
	choice,
	field,
	selectedStepId,
	onEditChoice,
	setEditing,
}) => {
	const [name, setName] = useState(choice.name);

	const handleSave = () => {
		setEditing(false);
		if (name !== choice.name) {
			onEditChoice(selectedStepId, field.id, choice.id, { name });
		}
	};

	return (
		<Stack direction="row" spacing={1} alignItems="center">
			<TextField
				size="small"
				value={name}
				onChange={(e) => setName(e.target.value)}
				inputProps={{
					"data-testid": `edit_choice_name_${field.id}_${choice.id}`,
				}}
			/>
			<IconButton
				size="small"
				onClick={handleSave}
				data-testid={`save_choice_${field.id}_${choice.id}`}
			>
				<Save fontSize="inherit" />
			</IconButton>
			<IconButton
				size="small"
				onClick={() => {
					setEditing(false);
					setName(choice.name);
				}}
			>
				<Close fontSize="inherit" />
			</IconButton>
		</Stack>
	);
};

interface RenderChoiceProps {
	config: WizardFormConfig;
	choice: Choice;
	field: Field;
	sortedChoices: Choice[];
	selectedStepId: number;
	onDeleteChoice: (stepId: number, fieldId: number, choiceId: number) => void;
	onMoveChoiceUp: (stepId: number, fieldId: number, choiceId: number) => void;
	onMoveChoiceDown: (stepId: number, fieldId: number, choiceId: number) => void;
	onEditChoice: (
		stepId: number,
		fieldId: number,
		choiceId: number,
		updates: Partial<Choice>,
	) => void;
}

const RenderChoice: React.FC<RenderChoiceProps> = ({
	choice,
	field,
	sortedChoices,
	selectedStepId,
	onDeleteChoice,
	onMoveChoiceDown,
	onMoveChoiceUp,
	onEditChoice,
	config,
}) => {
	const [editing, setEditing] = useState(false);

	if (editing) {
		return (
			<Box sx={{ pl: 4, display: "flex", alignItems: "center" }}>
				<EditChoice
					choice={choice}
					field={field}
					selectedStepId={selectedStepId}
					onEditChoice={onEditChoice}
					setEditing={setEditing}
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				pl: 4,
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
			}}
		>
			<Chip size="small" label={choice.name} />
			<Stack direction="row" spacing={0.5}>
				<IconButton
					size="small"
					disabled={choice.index === 0}
					onClick={() => onMoveChoiceUp(selectedStepId, field.id, choice.id)}
					data-testid={`move_choice_up_${field.id}_${choice.id}`}
				>
					<ArrowUpward fontSize="small" />
				</IconButton>
				<IconButton
					size="small"
					disabled={choice.index === sortedChoices.length - 1}
					onClick={() => onMoveChoiceDown(selectedStepId, field.id, choice.id)}
					data-testid={`move_choice_down_${field.id}_${choice.id}`}
				>
					<ArrowDownward fontSize="small" />
				</IconButton>
				<IconButton
					size="small"
					onClick={() => setEditing(true)}
					data-testid={`edit_choice_${field.id}_${choice.id}`}
				>
					<Edit fontSize="small" />
				</IconButton>
				<Tooltip
					title={
						stepsUsingChoiceAsCondition(
							config,
							selectedStepId,
							field.id,
							choice.id,
						).length > 0
							? `Can't delete this choice because it is used as trigger condition by: ${stepsUsingChoiceAsCondition(config, selectedStepId, field.id, choice.id).join(", ")}`
							: ""
					}
					data-testid={`delete_choice_${field.id}_${choice.id}_tooltip`}
				>
					<span>
						<IconButton
							size="small"
							onClick={() =>
								onDeleteChoice(selectedStepId, field.id, choice.id)
							}
							disabled={
								stepsUsingChoiceAsCondition(
									config,
									selectedStepId,
									field.id,
									choice.id,
								).length > 0
							}
							data-testid={`delete_choice_${field.id}_${choice.id}`}
						>
							<Delete fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</Stack>
		</Box>
	);
};

interface RenderSelectFieldProps {
	config: WizardFormConfig;
	field: Field;
	sortedChoices: Choice[];
	selectedStepId: number;
	onAddChoice: (stepId: number, fieldId: number) => void;
	onDeleteChoice: (stepId: number, fieldId: number, choiceId: number) => void;
	onMoveChoiceUp: (stepId: number, fieldId: number, choiceId: number) => void;
	onMoveChoiceDown: (stepId: number, fieldId: number, choiceId: number) => void;
	onEditChoice: (
		stepId: number,
		fieldId: number,
		choiceId: number,
		updates: Partial<Choice>,
	) => void;
}

const renderSelectField = (props: RenderSelectFieldProps) => {
	const {
		field,
		sortedChoices,
		selectedStepId,
		onAddChoice,
		onDeleteChoice,
		onMoveChoiceUp,
		onMoveChoiceDown,
		onEditChoice,
		config,
	} = props;

	return (
		<ListItemButton>
			<ListItemText
				primary={
					<Typography variant="body2" component="span" color="text.primary">
						({field.type}) {field.description}
						<Stack direction="column" spacing={1} flexWrap="wrap">
							{sortedChoices.map((choice) => (
								<RenderChoice
									key={choice.id}
									choice={choice}
									field={field}
									sortedChoices={sortedChoices}
									onDeleteChoice={onDeleteChoice}
									onMoveChoiceDown={onMoveChoiceDown}
									onMoveChoiceUp={onMoveChoiceUp}
									selectedStepId={selectedStepId}
									onEditChoice={onEditChoice}
									config={config}
								/>
							))}
							{(!field.choices || field.choices.length === 0) && (
								<Typography variant="caption" color="text.secondary">
									No choices
								</Typography>
							)}
							<Button
								size="small"
								startIcon={<Add />}
								onClick={(e) => {
									e.stopPropagation();
									onAddChoice(selectedStepId, field.id);
								}}
								data-testid={`add_choice_${field.id}`}
							>
								Add choice
							</Button>
						</Stack>
					</Typography>
				}
			/>
		</ListItemButton>
	);
};

interface RenderTextFieldProps {
	field: Field;
}

const renderTextField = (props: RenderTextFieldProps) => {
	const { field } = props;
	return (
		<ListItemButton>
			<ListItemText primary={`(${field.type}) ${field.description}`} />
		</ListItemButton>
	);
};

interface EditFieldProps {
	config: WizardFormConfig;
	setEditing: React.Dispatch<React.SetStateAction<boolean>>;
	field: Field;
	selectedStepId: number;
	onEditField: (
		stepId: number,
		fieldId: number,
		updates: Partial<Field>,
	) => void;
}

const EditField = (props: EditFieldProps) => {
	const { setEditing, field, selectedStepId, onEditField, config } = props;

	const [description, setDescription] = useState(field.description);
	const [required, setRequired] = useState(field.required);

	const handleSave = () => {
		setEditing(false);
		if (description !== field.description || required !== field.required) {
			onEditField(selectedStepId, field.id, { description, required });
		}
	};
	return (
		<Box display="flex" flexDirection="column" gap={1} sx={{ width: "100%" }}>
			<TextField
				size="small"
				label="Description"
				multiline
				minRows={4}
				value={description}
				onChange={(e) => setDescription(e.target.value)}
				inputProps={{ "data-testid": `edit_field_description_${field.id}` }}
			/>
			<Tooltip
				title={
					stepsUsingFieldAsCondition(config, selectedStepId, field.id).length >
					0
						? `This field must be required because it is used as trigger condition by: ${stepsUsingFieldAsCondition(config, selectedStepId, field.id).join(", ")}`
						: ""
				}
				data-testid={`edit_field_required_${field.id}_tooltip`}
			>
				<span>
					<FormControlLabel
						control={
							<Checkbox
								disabled={
									stepsUsingFieldAsCondition(config, selectedStepId, field.id)
										.length > 0
								}
								checked={required}
								onChange={(e) => setRequired(e.target.checked)}
								inputProps={
									{
										"data-testid": `edit_field_required_${field.id}`,
									} as React.InputHTMLAttributes<HTMLInputElement>
								}
							/>
						}
						label="Required"
					/>
				</span>
			</Tooltip>
			<Box display="flex" gap={1}>
				<Button
					size="small"
					startIcon={<Save />}
					variant="contained"
					onClick={handleSave}
				>
					Save
				</Button>
				<Button
					size="small"
					startIcon={<Close />}
					onClick={() => {
						setEditing(false);
						setDescription(field.description);
						setRequired(field.required);
					}}
				>
					Cancel
				</Button>
			</Box>
		</Box>
	);
};

interface RenderFieldProps {
	config: WizardFormConfig;
	field: Field;
	selectedStepId: number;
	onAddChoice: (stepId: number, fieldId: number) => void;
	onDeleteChoice: (stepId: number, fieldId: number, choiceId: number) => void;
	onMoveChoiceUp: (stepId: number, fieldId: number, choiceId: number) => void;
	onMoveChoiceDown: (stepId: number, fieldId: number, choiceId: number) => void;
	onEditField: (
		stepId: number,
		fieldId: number,
		updates: Partial<Field>,
	) => void;
	onMoveFieldUp: (stepId: number, fieldId: number) => void;
	onMoveFieldDown: (stepId: number, fieldId: number) => void;
	onDeleteField: (stepId: number, fieldId: number) => void;
	onEditChoice: (
		stepId: number,
		fieldId: number,
		choiceId: number,
		updates: Partial<Choice>,
	) => void;
}

const RenderField = (props: RenderFieldProps) => {
	const {
		field,
		selectedStepId,
		onAddChoice,
		onDeleteChoice,
		onMoveChoiceUp,
		onMoveChoiceDown,
		onEditField,
		onMoveFieldUp,
		onMoveFieldDown,
		onDeleteField,
		onEditChoice,
		config,
	} = props;
	const [editing, setEditing] = useState(false);

	const fields = fieldsFromStepId(config, selectedStepId);

	if (editing) {
		return (
			<EditField
				config={config}
				selectedStepId={selectedStepId}
				setEditing={setEditing}
				onEditField={onEditField}
				field={field}
			/>
		);
	}

	const content = match(field.type)
		.with("text", () => renderTextField({ field }))
		.with("select", () => {
			const sortedChoices = getChoicesFromField(field);
			return renderSelectField({
				field,
				sortedChoices,
				selectedStepId,
				onAddChoice,
				onDeleteChoice,
				onMoveChoiceUp,
				onMoveChoiceDown,
				onEditChoice,
				config,
			});
		})
		.with("long_text", () => renderTextField({ field }))
		.with("file", () => renderTextField({ field }))
		.exhaustive();

	return (
		<ListItem
			key={field.id}
			sx={{
				borderRadius: 1,
				mb: 0.5,
				p: 0,
			}}
		>
			<Box display="flex" flexDirection="row" sx={{ width: "100%" }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<IconButton
						edge="end"
						size="small"
						disabled={field.index === 0}
						onClick={() => onMoveFieldUp(selectedStepId, field.id)}
						data-testid={`move_field_up_${field.id}`}
					>
						<ArrowUpward fontSize="small" />
					</IconButton>
					<IconButton
						edge="end"
						size="small"
						disabled={field.index === fields.length - 1}
						onClick={() => onMoveFieldDown(selectedStepId, field.id)}
						data-testid={`move_field_down_${field.id}`}
					>
						<ArrowDownward fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							setEditing(true);
						}}
						data-testid={`edit_field_${field.id}`}
					>
						<Edit fontSize="small" />
					</IconButton>
					<Tooltip
						title={
							stepsUsingFieldAsCondition(config, selectedStepId, field.id)
								.length > 0
								? `Can't delete this field because it is used as trigger condition by: ${stepsUsingFieldAsCondition(config, selectedStepId, field.id).join(", ")}`
								: ""
						}
						data-testid={`delete_field_${field.id}_tooltip`}
					>
						<span>
							<IconButton
								edge="end"
								size="small"
								onClick={() => onDeleteField(selectedStepId, field.id)}
								disabled={
									stepsUsingFieldAsCondition(config, selectedStepId, field.id)
										.length > 0
								}
								data-testid={`delete_field_${field.id}`}
							>
								<Delete fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Box>
				{content}
			</Box>
		</ListItem>
	);
};

interface AddFieldProps {
	onAddField: (stepId: number, type: Field["type"]) => void;
	selectedStepId: number;
}
const AddFieldButton = (props: AddFieldProps) => {
	const { onAddField, selectedStepId } = props;
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const handleSelectType = (type: Field["type"]) => {
		if (selectedStepId !== null) {
			onAddField(selectedStepId, type);
		}
		setAnchorEl(null);
	};

	return (
		<>
			<Button
				size="small"
				variant="outlined"
				startIcon={<Add />}
				onClick={handleOpenMenu}
			>
				Add Field
			</Button>

			<Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
				{fieldTypeSchema.options.map((option) => {
					return (
						<MenuItem key={option} onClick={() => handleSelectType(option)}>
							{option}
						</MenuItem>
					);
				})}
			</Menu>
		</>
	);
};

interface StepFieldsProps {
	config: WizardFormConfig;
	selectedStepId: number | null;
	onAddField: (stepId: number, type: Field["type"]) => void;
	onDeleteField: (stepId: number, fieldId: number) => void;
	onAddChoice: (stepId: number, fieldId: number) => void;
	onDeleteChoice: (stepId: number, fieldId: number, choiceId: number) => void;
	onMoveFieldUp: (stepId: number, fieldId: number) => void;
	onMoveFieldDown: (stepId: number, fieldId: number) => void;
	onMoveChoiceUp: (stepId: number, fieldId: number, choiceId: number) => void;
	onMoveChoiceDown: (stepId: number, fieldId: number, choiceId: number) => void;
	onEditField: (
		stepId: number,
		fieldId: number,
		updates: Partial<Field>,
	) => void;
	onEditChoice: (
		stepId: number,
		fieldId: number,
		choiceId: number,
		updates: Partial<Choice>,
	) => void;
}

export const StepFields: React.FC<StepFieldsProps> = ({
	config,
	selectedStepId,
	onDeleteField,
	onAddChoice,
	onDeleteChoice,
	onAddField,
	onMoveFieldDown,
	onMoveFieldUp,
	onMoveChoiceUp,
	onMoveChoiceDown,
	onEditField,
	onEditChoice,
}) => {
	if (selectedStepId === null) return null;

	const fields = fieldsFromStepId(config, selectedStepId);

	return (
		<CardContent sx={{ pt: 1.5 }}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={1}
			>
				<Typography variant="h6" sx={{ fontSize: "1rem" }}>
					Fields
				</Typography>
				<AddFieldButton
					onAddField={onAddField}
					selectedStepId={selectedStepId}
				/>
			</Box>
			{fields.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					No fields for this step.
				</Typography>
			) : (
				<List disablePadding>
					{fields.map((field) => {
						return (
							<RenderField
								key={field.id}
								field={field}
								onAddChoice={onAddChoice}
								onDeleteChoice={onDeleteChoice}
								onMoveChoiceUp={onMoveChoiceUp}
								onMoveChoiceDown={onMoveChoiceDown}
								selectedStepId={selectedStepId}
								onEditField={onEditField}
								config={config}
								onMoveFieldDown={onMoveFieldDown}
								onMoveFieldUp={onMoveFieldUp}
								onDeleteField={onDeleteField}
								onEditChoice={onEditChoice}
							/>
						);
					})}
				</List>
			)}
		</CardContent>
	);
};
